// src/components/ToastHost.jsx
//
// App-wide toast renderer. Mounted in App.jsx alongside ImpersonationBanner
// — once in each render tree (platform admin layout AND standard shell)
// so toasts are reachable from any code path.
//
// Top-centre anchored, newer toasts below older. (This comment said
// "bottom-right" until 2026-08-31; the component has been top-centre for as
// long as git records it.)
//
// z-[9000] sits above modals (z-[300]). It used to be documented as sitting
// BELOW ImpersonationBanner's z-[9999] so the impersonation indicator always
// remained visible. That z-index no longer exists — the banners are in normal
// flow now — and the intent is better served than it was: a flow element
// cannot be covered by an overlay at all, so no z-index has to be reserved
// above this one to protect it.
//
// ── THE OFFSET FOLLOWS FLOW. 2026-08-31. ───────────────────────────────────
//
// This was `fixed top-16` — 64px, a viewport measurement chosen against the
// h-14 (56px) header for an 8px clearance. It worked only because
// ImpersonationBanner was `fixed` and therefore did not move the header. Once
// the banners displace the header, a viewport literal is wrong by exactly the
// banner height, and "swap 16 for a bigger number" would be wrong again the
// moment a second banner co-occurs or the text wraps.
//
// So the outer element is now a ZERO-HEIGHT FLOW CHILD mounted immediately
// after the header: `relative h-0`. It contributes nothing to layout, but its
// origin IS the header's bottom edge, because flow put it there. The toast
// list is `absolute top-2` inside it.
//
// `top-2` is not a measurement of anything else — it is the 8px gap itself,
// the one value that was ever a design choice. Nothing here encodes the height
// of the header, of a banner, or of two banners at once.
//
// Each toast has: tone (per type), lucide icon, message text, X dismiss.
// No animation library — tailwind `transition` + initial opacity-0 is
// enough for a soft fade-in. Dismissal is instant (removed from store).

import { CheckCircle2, Info, AlertTriangle, AlertCircle, X as XIcon } from "lucide-react";
import { useToastStore } from "../stores/useToastStore";

const TONES = {
  success: "bg-emerald-500/15 border-emerald-500/50 text-emerald-100",
  info:    "bg-slate-700/30   border-slate-600/50   text-slate-100",
  warning: "bg-amber-500/15   border-amber-500/50   text-amber-100",
  error:   "bg-red-500/15     border-red-500/50     text-red-100",
};

const ICONS = {
  success: CheckCircle2,
  info:    Info,
  warning: AlertTriangle,
  error:   AlertCircle,
};

export default function ToastHost() {
  const toasts = useToastStore((s) => s.toasts);
  const dismissToast = useToastStore((s) => s.dismissToast);

  if (toasts.length === 0) return null;

  return (
    <div className="relative h-0 shrink-0" aria-hidden={false}>
    <div
      className="absolute top-2 left-1/2 -translate-x-1/2 z-[9000] flex flex-col gap-2
                 pointer-events-none print:hidden"
      role="region"
      aria-label="Notifications"
    >
      {toasts.map((t) => {
        const Icon = ICONS[t.type] ?? Info;
        const clickable = typeof t.onClick === "function";
        // When clickable, the icon+message region invokes onClick and
        // dismisses; the X button keeps its own dismiss-only handler
        // (stopPropagation prevents the parent click from firing too).
        const handleClick = clickable
          ? () => { try { t.onClick(); } finally { dismissToast(t.id); } }
          : undefined;
        return (
          <div
            key={t.id}
            role={t.type === "error" || t.type === "warning" ? "alert" : "status"}
            aria-live={t.type === "error" || t.type === "warning" ? "assertive" : "polite"}
            className={`pointer-events-auto flex items-start gap-2
                        min-w-[260px] max-w-sm px-3 py-2 rounded-md border
                        shadow-lg shadow-black/40 backdrop-blur-sm
                        text-[11px] leading-snug
                        ${TONES[t.type] ?? TONES.info}`}
          >
            <div
              role={clickable ? "button" : undefined}
              tabIndex={clickable ? 0 : undefined}
              onClick={handleClick}
              onKeyDown={clickable ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleClick();
                }
              } : undefined}
              className={`flex items-start gap-2 flex-1 min-w-0
                          ${clickable ? "cursor-pointer hover:opacity-90 transition" : ""}`}
              title={clickable ? "Click to open" : undefined}
            >
              <Icon size={13} className="mt-[1px] shrink-0" />
              <div className="flex-1 break-words whitespace-pre-line">{t.message}</div>
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                dismissToast(t.id);
              }}
              className="shrink-0 opacity-70 hover:opacity-100 transition"
              title="Dismiss"
              aria-label="Dismiss notification"
            >
              <XIcon size={12} />
            </button>
          </div>
        );
      })}
    </div>
    </div>
  );
}
